import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { productAPI, platformAPI, brandAPI, enumAPI } from '../../services/api';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import SearchableSelect from '../../components/common/SearchableSelect';

const createEmptyFeedbackConfig = () => ({
  localId: `${Date.now()}-${Math.random()}`,
  feedbackType: '',
  payoutType: 'LESS',
  payoutValue: '',
  totalSlots: '',
  remainingSlots: '',
  remainingTouched: false
});

const formatFeedbackLabel = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : 'Feedback Type';

const ImageField = ({ label, value, onChange, onPickFile, onClear, fileName, inputId, uploadLabel = 'Choose File' }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>{label}</label>
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Paste image URL or upload below"
        style={{ flex: 1, padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: '#ffffff', color: 'var(--text)', fontSize: '14px' }}
      />
      <label
        htmlFor={inputId}
        style={{
          padding: '11px 14px',
          borderRadius: '8px',
          border: '1px solid #d1d5db',
          background: '#f8fafc',
          fontSize: '13px',
          fontWeight: '600',
          color: '#475569',
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {uploadLabel}
      </label>
      <input id={inputId} type="file" accept="image/*" onChange={onPickFile} style={{ display: 'none' }} />
      {(value || fileName) && (
        <button
          type="button"
          onClick={onClear}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: '1px solid #fecaca',
            background: '#fff1f2',
            color: '#dc2626',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Remove image"
        >
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
    {fileName && <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>{fileName}</div>}
    {value && (
      <div style={{ marginTop: '10px', width: '72px', height: '72px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <img src={value} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    )}
  </div>
);

const CreateProduct = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(true);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [brandMode, setBrandMode] = useState('existing');
  const [dealFeedbackTypes, setDealFeedbackTypes] = useState([]);
  const [dealOrderTypes, setDealOrderTypes] = useState([]);
  const [loadingEnums, setLoadingEnums] = useState(false);
  const [feedbackConfigs, setFeedbackConfigs] = useState([createEmptyFeedbackConfig()]);
  const [feedbackConfigError, setFeedbackConfigError] = useState('');
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: {
      active: 'true',
      productAmount: '',
      restrictedToAssignedMediators: false,
      imageUrl: ''
    }
  });

  const restrictedToAssignedMediators = watch('restrictedToAssignedMediators');
  const productImageUrl = watch('imageUrl');
  const selectedPlatformId = watch('platformId');
  const selectedBrandId = watch('brandId');
  const selectedDealOrderType = watch('dealOrderType');

  useEffect(() => {
    fetchPlatforms();
    fetchBrands();
    fetchEnums();
  }, []);

  const feedbackTypeOptions = useMemo(
    () =>
      dealFeedbackTypes.map((type) => {
        const value = typeof type === 'string' ? type : type.code;
        const label = typeof type === 'string' ? formatFeedbackLabel(type) : type.label || formatFeedbackLabel(type.code);
        return { value, label };
      }),
    [dealFeedbackTypes]
  );
  const platformOptions = platforms.map((platform) => ({ value: platform.id || platform.platformId, label: platform.platformName }));
  const brandOptions = brands.map((brand) => ({ value: brand.id || brand.brandId, label: brand.brandName }));
  const dealOrderTypeOptions = dealOrderTypes.map((type) => {
    const value = typeof type === 'string' ? type : type.code;
    const label = typeof type === 'string' ? formatFeedbackLabel(type) : type.label || formatFeedbackLabel(type.code);
    return { value, label };
  });

  const fetchEnums = async () => {
    setLoadingEnums(true);
    try {
      const [feedbackTypesResponse, orderTypesResponse] = await Promise.all([
        enumAPI.getDealFeedbackType(),
        enumAPI.getDealOrderType()
      ]);

      setDealFeedbackTypes(feedbackTypesResponse.data || []);
      setDealOrderTypes(orderTypesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch enums:', error);
      toast.error('Failed to load enum values');
    } finally {
      setLoadingEnums(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      setLoadingPlatforms(true);
      const response = await platformAPI.getPlatforms();
      setPlatforms(response.data.content || response.data || []);
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
      toast.error('Failed to load platforms');
    } finally {
      setLoadingPlatforms(false);
    }
  };

  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await brandAPI.getActiveBrands();
      setBrands(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
      toast.error('Failed to load brands');
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleProductImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProductImageFile(file);
    setProductImagePreview(URL.createObjectURL(file));
    event.target.value = '';
  };

  const clearProductImage = () => {
    setProductImageFile(null);
    setProductImagePreview('');
    setValue('imageUrl', '');
  };

  const handleBack = () => {
    navigate('/admin/products');
  };

  const handleBrandModeChange = (mode) => {
    setBrandMode(mode);
    if (mode === 'existing') {
      setValue('newBrandName', '');
    } else {
      setValue('brandId', '');
    }
  };

  const updateFeedbackConfig = (localId, field, value) => {
    setFeedbackConfigError('');
    setFeedbackConfigs((current) =>
      current.map((config) => {
        if (config.localId !== localId) return config;

        const nextConfig = { ...config, [field]: value };
        if (field === 'remainingSlots') {
          nextConfig.remainingTouched = true;
        }
        if (field === 'totalSlots' && !config.remainingTouched && (config.remainingSlots === '' || config.remainingSlots === config.totalSlots)) {
          nextConfig.remainingSlots = value;
        }
        return nextConfig;
      })
    );
  };

  const addFeedbackConfig = () => {
    setFeedbackConfigError('');
    setFeedbackConfigs((current) => [...current, createEmptyFeedbackConfig()]);
  };

  const removeFeedbackConfig = (localId) => {
    setFeedbackConfigError('');
    setFeedbackConfigs((current) => (current.length === 1 ? current : current.filter((config) => config.localId !== localId)));
  };

  const validateFeedbackConfigs = () => {
    if (!feedbackConfigs.length) {
      return 'Add at least one feedback configuration.';
    }

    const usedTypes = new Set();

    for (const config of feedbackConfigs) {
      if (!config.feedbackType) {
        return 'Select a feedback type for every feedback configuration.';
      }
      if (usedTypes.has(config.feedbackType)) {
        return `Duplicate feedback type "${formatFeedbackLabel(config.feedbackType)}" is not allowed.`;
      }
      usedTypes.add(config.feedbackType);

      if (!config.payoutType) {
        return `Select a payout type for ${formatFeedbackLabel(config.feedbackType)}.`;
      }

      const payoutValue = Number(config.payoutValue);
      const totalSlots = Number(config.totalSlots);
      const remainingSlots = Number(config.remainingSlots === '' ? config.totalSlots : config.remainingSlots);

      if (Number.isNaN(payoutValue) || payoutValue < 0) {
        return `Enter a valid less value for ${formatFeedbackLabel(config.feedbackType)}.`;
      }

      if (Number.isNaN(totalSlots) || totalSlots < 0) {
        return `Enter valid total slots for ${formatFeedbackLabel(config.feedbackType)}.`;
      }

      if (Number.isNaN(remainingSlots) || remainingSlots < 0 || remainingSlots > totalSlots) {
        return `Remaining slots must be between 0 and total slots for ${formatFeedbackLabel(config.feedbackType)}.`;
      }
    }

    return '';
  };

  const onSubmit = async (data) => {
    const feedbackValidationMessage = validateFeedbackConfigs();
    if (feedbackValidationMessage) {
      setFeedbackConfigError(feedbackValidationMessage);
      toast.error(feedbackValidationMessage);
      return;
    }

    setSubmitting(true);
    try {
      const selectedPlatform = platforms.find((platform) => String(platform.id || platform.platformId) === String(data.platformId));
      const selectedBrand = brands.find((brand) => String(brand.id || brand.brandId) === String(data.brandId));

      const productData = {
        productCode: data.productCode,
        productName: data.productName,
        asinNumber: data.asinNumber || '',
        link: data.link || '',
        active: data.active === 'true' || data.active === true,
        dealOrderType: data.dealOrderType,
        productAmount: parseFloat(data.productAmount),
        restrictedToAssignedMediators: !!data.restrictedToAssignedMediators,
        platformId: data.platformId,
        platformName: selectedPlatform?.platformName || '',
        sellerFeedback: !!data.sellerFeedback,
        returnWindowClose: !!data.returnWindowClose,
        screenRecording: !!data.screenRecording,
        feedbackConfigs: feedbackConfigs.map((config) => ({
          feedbackType: config.feedbackType,
          payoutType: config.payoutType,
          payoutValue: parseFloat(config.payoutValue || 0),
          totalSlots: parseInt(config.totalSlots, 10) || 0,
          remainingSlots: parseInt(config.remainingSlots === '' ? config.totalSlots : config.remainingSlots, 10) || 0
        }))
      };

      if (brandMode === 'existing' && data.brandId) {
        productData.brandId = data.brandId;
        productData.brandName = selectedBrand?.brandName || '';
      } else if (brandMode === 'new' && data.newBrandName) {
        productData.brandName = data.newBrandName;
      }

      const requestBody = new FormData();
      requestBody.append('data', new Blob([JSON.stringify(productData)], { type: 'application/json' }));
      if (productImageFile) {
        requestBody.append('image', productImageFile);
      }

      await productAPI.createProduct(requestBody);

      toast.success('Product created successfully!');
      reset({
        active: 'true',
        productAmount: '',
        restrictedToAssignedMediators: false,
        imageUrl: ''
      });
      setProductImageFile(null);
      setProductImagePreview('');
      setFeedbackConfigs([createEmptyFeedbackConfig()]);
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.message || 'Failed to create product. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'auto', minHeight: 0 }}>
      <div style={{ maxWidth: '980px', margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <button
            onClick={handleBack}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#ffffff', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-sub)', cursor: 'pointer', fontSize: '14px' }}
          >
            <Icon name="arrow-left" size={16} />
            Back
          </button>
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: '700', fontSize: '24px', color: 'var(--text)', margin: 0 }}>Create Product</h1>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>Add a product with payout rules, slots, and optional images.</p>
          </div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Product Name *</label>
                <input {...register('productName', { required: 'Product name is required' })} type="text" placeholder="e.g., iPhone 15" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', borderColor: errors.productName ? 'var(--red)' : 'var(--border)' }} />
                {errors.productName && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.productName.message}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Product Code *</label>
                <input {...register('productCode', { required: 'Product code is required' })} type="text" placeholder="PROD001" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', borderColor: errors.productCode ? 'var(--red)' : 'var(--border)' }} />
                {errors.productCode && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.productCode.message}</p>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>ASIN Number</label>
                <input {...register('asinNumber')} type="text" placeholder="e.g., B0XXXXXXX" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Product Link *</label>
              <input {...register('link', { required: 'Product link is required' })} type="text" placeholder="https://example.com/product" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', borderColor: errors.link ? 'var(--red)' : 'var(--border)' }} />
              {errors.link && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.link.message}</p>}
            </div>

            <ImageField
              label="Product Image"
              value={productImagePreview || productImageUrl}
              onChange={(value) => { setProductImageFile(null); setProductImagePreview(''); setValue('imageUrl', value); }}
              onPickFile={handleProductImageUpload}
              onClear={clearProductImage}
              fileName={productImageFile?.name}
              inputId="product-image-upload"
              uploadLabel="Choose File"
            />
            <input type="hidden" {...register('imageUrl')} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Platform *</label>
                {loadingPlatforms ? (
                  <div style={{ padding: '12px', color: 'var(--text-muted)' }}>Loading platforms...</div>
                ) : (
                  <>
                    <input type="hidden" {...register('platformId', { required: 'Platform is required' })} />
                    <SearchableSelect
                      options={platformOptions}
                      value={selectedPlatformId}
                      onChange={(value) => setValue('platformId', value, { shouldValidate: true, shouldDirty: true })}
                      placeholder="Select a platform"
                      searchPlaceholder="Type platform name..."
                      error={Boolean(errors.platformId)}
                    />
                  </>
                )}
                {errors.platformId && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.platformId.message}</p>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Deal Order Type *</label>
                <input type="hidden" {...register('dealOrderType', { required: 'Deal order type is required' })} />
                <SearchableSelect
                  options={dealOrderTypeOptions}
                  value={selectedDealOrderType}
                  onChange={(value) => setValue('dealOrderType', value, { shouldValidate: true, shouldDirty: true })}
                  placeholder={loadingEnums ? 'Loading order types...' : 'Select order type'}
                  searchPlaceholder="Type order type..."
                  disabled={loadingEnums}
                  error={Boolean(errors.dealOrderType)}
                />
                {errors.dealOrderType && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.dealOrderType.message}</p>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Product Amount *</label>
                <input {...register('productAmount', { required: 'Product amount is required', validate: (value) => (parseFloat(value) > 0 ? true : 'Product amount must be greater than 0') })} type="number" step="0.01" placeholder="999.00" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', borderColor: errors.productAmount ? 'var(--red)' : 'var(--border)' }} />
                {errors.productAmount && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.productAmount.message}</p>}
              </div>
            </div>

            <div style={{ padding: '18px', borderRadius: '12px', border: '1px solid #dbeafe', background: '#f8fbff' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                <input {...register('restrictedToAssignedMediators')} type="checkbox" style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--primary)' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Restrict to selected mediators</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-sub)', marginTop: '4px' }}>
                    {restrictedToAssignedMediators ? 'Only assigned mediators can submit this deal.' : 'All active mediators can submit this deal — no manual assignment needed.'}
                  </div>
                </div>
              </label>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Brand *</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', padding: '4px', background: '#f1f5f9', borderRadius: '8px' }}>
                <button type="button" onClick={() => handleBrandModeChange('existing')} style={{ flex: 1, padding: '8px 16px', border: 'none', borderRadius: '6px', background: brandMode === 'existing' ? 'var(--primary)' : 'transparent', color: brandMode === 'existing' ? 'white' : 'var(--text-sub)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Existing Brand</button>
                <button type="button" onClick={() => handleBrandModeChange('new')} style={{ flex: 1, padding: '8px 16px', border: 'none', borderRadius: '6px', background: brandMode === 'new' ? 'var(--primary)' : 'transparent', color: brandMode === 'new' ? 'white' : 'var(--text-sub)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>New Brand</button>
              </div>

              {brandMode === 'existing' ? (
                <>
                  {loadingBrands ? (
                    <div style={{ padding: '12px', color: 'var(--text-muted)' }}>Loading brands...</div>
                  ) : (
                    <>
                      <input type="hidden" {...register('brandId', { required: 'Brand is required when using existing brand' })} />
                      <SearchableSelect
                        options={brandOptions}
                        value={selectedBrandId}
                        onChange={(value) => setValue('brandId', value, { shouldValidate: true, shouldDirty: true })}
                        placeholder="Select a brand"
                        searchPlaceholder="Type brand name..."
                        error={Boolean(errors.brandId)}
                      />
                    </>
                  )}
                  {errors.brandId && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.brandId.message}</p>}
                </>
              ) : (
                <>
                  <input {...register('newBrandName', { required: 'Brand name is required when creating new brand' })} type="text" placeholder="Enter new brand name" style={{ width: '100%', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', borderColor: errors.newBrandName ? 'var(--red)' : 'var(--border)' }} />
                  {errors.newBrandName && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.newBrandName.message}</p>}
                </>
              )}
            </div>

            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>Feedback Configurations</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-sub)' }}>Each feedback type needs its own payout mode, less value, and slots.</p>
                </div>
                <Button type="button" size="sm" onClick={addFeedbackConfig}>
                  <Icon name="plus" size={14} style={{ marginRight: '8px' }} />
                  Add Row
                </Button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {feedbackConfigs.map((config, index) => (
                  <div key={config.localId} style={{ padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    {(() => {
                      const usedTypes = new Set(
                        feedbackConfigs
                          .filter((item) => item.localId !== config.localId && item.feedbackType)
                          .map((item) => item.feedbackType)
                      );
                      return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr 0.9fr 0.8fr 0.8fr auto', gap: '12px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Feedback Type *</label>
                        <select value={config.feedbackType} onChange={(event) => updateFeedbackConfig(config.localId, 'feedbackType', event.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
                          <option value="">Select type</option>
                          {feedbackTypeOptions.map((option) => <option key={option.value} value={option.value} disabled={usedTypes.has(option.value)}>{option.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Payout Type *</label>
                        <select value={config.payoutType} onChange={(event) => updateFeedbackConfig(config.localId, 'payoutType', event.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
                          <option value="LESS">LESS</option>
                          <option value="COMMISSION">COMMISSION</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Less Value *</label>
                        <input type="number" step="0.01" value={config.payoutValue} onChange={(event) => updateFeedbackConfig(config.localId, 'payoutValue', event.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Total Slots *</label>
                        <input type="number" min="0" value={config.totalSlots} onChange={(event) => updateFeedbackConfig(config.localId, 'totalSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Remaining Slots *</label>
                        <input type="number" min="0" value={config.remainingSlots} onChange={(event) => updateFeedbackConfig(config.localId, 'remainingSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
                      </div>
                      <button type="button" onClick={() => removeFeedbackConfig(config.localId)} disabled={feedbackConfigs.length === 1} style={{ width: '38px', height: '38px', borderRadius: '10px', border: '1px solid #fecaca', background: feedbackConfigs.length === 1 ? '#f8fafc' : '#fff1f2', color: feedbackConfigs.length === 1 ? '#94a3b8' : '#ef4444', cursor: feedbackConfigs.length === 1 ? 'not-allowed' : 'pointer' }} title={feedbackConfigs.length === 1 ? 'At least one feedback config is required' : `Remove row ${index + 1}`}>
                        <Icon name="trash-2" size={15} />
                      </button>
                    </div>
                      );
                    })()}

                  </div>
                ))}
              </div>

              {feedbackConfigError && <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '10px' }}>{feedbackConfigError}</p>}
            </div>

            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>Additional Settings</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input {...register('sellerFeedback')} type="checkbox" style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                  <span style={{ fontSize: '14px', color: 'var(--text)' }}>Enable Seller Feedback</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input {...register('returnWindowClose')} type="checkbox" style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                  <span style={{ fontSize: '14px', color: 'var(--text)' }}>Close Return Window</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input {...register('screenRecording')} type="checkbox" style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                  <span style={{ fontSize: '14px', color: 'var(--text)' }}>Enable Screen Recording</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
              <Button type="button" variant="secondary" size="md" onClick={handleBack} style={{ flex: 1 }}>Cancel</Button>
              <Button type="submit" variant="primary" size="md" loading={submitting} style={{ flex: 1 }}>Create Product</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProduct;
